// Phase 7 D3 — Track C T-C6. /settings/notifications page.
//
// Q-12 (per-alert-type granularity): the alert types come from the module
// registry. v2.0 has no `/api/modules` discovery endpoint that returns
// alert-type metadata, so we mirror the registry catalogue inline here —
// each entry corresponds to a real `alertTypes[].id` declared by an active
// module subsystem. When a backend module is added later, this catalogue
// gets a new entry; when one is removed, its key falls into the
// "unknown keys" bucket and the user can clear it (Q-12 unknown-key path).
//
// Off by default: a missing key in the server's `notifications_json` map
// reads as `false` — explicit user opt-in toggles flip a key to `true`.
//
// Copy includes the AC-4 disclaimer: "for future mobile push — not active
// in v2.0".

import * as React from 'react';
import { copy } from '@dailydeck/shared/copy';
import type { NotificationsMap } from '@dailydeck/shared/types';
import {
  usePreferences,
  useUpdatePreferences,
} from '../../../hooks/usePreferences.js';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../../components/ui/Card.js';
import { SkeletonRow } from '../../../components/ui/Skeleton.js';
import { NotificationToggle } from './NotificationToggle.js';

/**
 * Catalogue of alert types known to v2.0. Mirrors the `alertTypes` arrays
 * exported by each module subsystem under `src/modules/*`. Order matters
 * for the rendered list. The `section` discriminator drives the section
 * grouping below.
 */
interface AlertTypeEntry {
  id: string;
  label: string;
  description: string;
  section: 'payments' | 'dates';
}

const ALERT_TYPE_CATALOGUE: readonly AlertTypeEntry[] = [
  // Payments — recurring + statement + email + categorisation + insights
  {
    id: 'payment.due_soon',
    label: 'Payment due soon',
    description: 'A bill is due within the next few days.',
    section: 'payments',
  },
  {
    id: 'payment.due_today',
    label: 'Payment due today',
    description: 'A bill is due today.',
    section: 'payments',
  },
  {
    id: 'payment.overdue',
    label: 'Payment overdue',
    description: 'A bill is past its due date and not yet paid.',
    section: 'payments',
  },
  {
    id: 'subscription.renewal_upcoming',
    label: 'Subscription renewal upcoming',
    description: 'A subscription renews within the next 7 days.',
    section: 'payments',
  },
  {
    id: 'subscription.price_changed',
    label: 'Subscription price changed',
    description: 'A subscription price changed by more than 5%.',
    section: 'payments',
  },
  {
    id: 'recurring_payment.detected',
    label: 'Recurring payment detected',
    description: 'A new recurring payment cadence was detected.',
    section: 'payments',
  },
  {
    id: 'statement.import_completed',
    label: 'Statement import completed',
    description: 'A statement file was parsed successfully.',
    section: 'payments',
  },
  {
    id: 'statement.import_failed',
    label: 'Statement import failed',
    description: 'A statement file was rejected.',
    section: 'payments',
  },
  {
    id: 'email.bill_detected',
    label: 'Bill detected in email',
    description: 'A high-confidence bill was extracted from your inbox.',
    section: 'payments',
  },
  {
    id: 'categorization.needs_review',
    label: 'Categorisation needs review',
    description: 'A transaction was AI-categorised with low confidence.',
    section: 'payments',
  },
  {
    id: 'spend.ai_budget_exceeded',
    label: 'AI budget exceeded',
    description: 'Your monthly AI budget cap was reached.',
    section: 'payments',
  },
  {
    id: 'spend.insight_created',
    label: 'New spending insight',
    description: 'A new AI-generated insight was generated.',
    section: 'payments',
  },
  {
    id: 'spend.anomaly_detected',
    label: 'Spend anomaly detected',
    description: 'An unusual category-level spend pattern was detected.',
    section: 'payments',
  },
  // Dates
  {
    id: 'date.upcoming',
    label: 'Important date upcoming',
    description: 'A reminder offset has been reached for an upcoming date.',
    section: 'dates',
  },
  {
    id: 'date.today',
    label: 'Important date today',
    description: 'An important date is today.',
    section: 'dates',
  },
];

const KNOWN_KEYS = new Set<string>(ALERT_TYPE_CATALOGUE.map((e) => e.id));

export function Notifications(): JSX.Element {
  React.useEffect(() => {
    document.title = `DailyDeck — ${copy.settings.shell.nav.notifications}`;
  }, []);

  const query = usePreferences();
  const update = useUpdatePreferences();
  const [statusMessage, setStatusMessage] = React.useState<string | null>(null);

  const notifications: NotificationsMap = query.data?.notifications ?? {};

  function patchOne(key: string, next: boolean | null): void {
    setStatusMessage(null);
    const merged: NotificationsMap = { ...notifications };
    if (next === null) {
      delete merged[key];
    } else {
      merged[key] = next;
    }
    update.mutate(
      { notifications: merged },
      {
        onError: () => setStatusMessage(copy.settings.notifications.saveError),
      },
    );
  }

  // Identify "unknown" keys: keys present in the persisted map that are not
  // in the current registry catalogue. Q-12 says render them greyed-out
  // with a tooltip + a remove affordance, NOT silent drop.
  const unknownKeys = Object.keys(notifications).filter((k) => !KNOWN_KEYS.has(k));

  const paymentsEntries = ALERT_TYPE_CATALOGUE.filter((e) => e.section === 'payments');
  const datesEntries = ALERT_TYPE_CATALOGUE.filter((e) => e.section === 'dates');

  return (
    <div className="flex flex-col gap-6">
      <header>
        <h2
          id="settings-section-heading"
          className="text-title-2 font-semibold text-text-primary"
        >
          {copy.settings.notifications.title}
        </h2>
        <p className="text-body text-text-secondary mt-1">
          {copy.settings.notifications.description}
        </p>
      </header>

      {query.isPending && (
        <div className="rounded-lg border border-border-subtle bg-surface-raised">
          <SkeletonRow />
          <SkeletonRow />
        </div>
      )}

      {query.isSuccess && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>{copy.settings.notifications.sectionPayments}</CardTitle>
            </CardHeader>
            <CardContent>
              <ul role="list" className="flex flex-col">
                {paymentsEntries.map((entry) => (
                  <li key={entry.id}>
                    <NotificationToggle
                      alertTypeId={entry.id}
                      label={entry.label}
                      description={entry.description}
                      checked={notifications[entry.id] === true}
                      disabled={update.isPending}
                      onChange={(next) => patchOne(entry.id, next)}
                    />
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{copy.settings.notifications.sectionDates}</CardTitle>
            </CardHeader>
            <CardContent>
              <ul role="list" className="flex flex-col">
                {datesEntries.map((entry) => (
                  <li key={entry.id}>
                    <NotificationToggle
                      alertTypeId={entry.id}
                      label={entry.label}
                      description={entry.description}
                      checked={notifications[entry.id] === true}
                      disabled={update.isPending}
                      onChange={(next) => patchOne(entry.id, next)}
                    />
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {unknownKeys.length > 0 && (
            <Card data-testid="notifications-unknown-section">
              <CardHeader>
                <CardTitle>{copy.settings.notifications.sectionUnknown}</CardTitle>
                <CardDescription>
                  {copy.settings.notifications.unknownTooltip}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul role="list" className="flex flex-col">
                  {unknownKeys.map((key) => (
                    <li key={key}>
                      <NotificationToggle
                        alertTypeId={key}
                        label={key}
                        checked={notifications[key] === true}
                        disabled={update.isPending}
                        unknown
                        onChange={() => {
                          /* no-op for unknown */
                        }}
                        onRemoveUnknown={() => patchOne(key, null)}
                      />
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </>
      )}

      <p role="status" aria-live="polite" className="text-caption text-state-danger">
        {statusMessage ?? ''}
      </p>
    </div>
  );
}
